import { useTranslation } from "react-i18next";
import { Checkbox, Form, Icon } from "semantic-ui-react";

import { Element } from "components/ReactScroll/index";
import { createFormHelper } from "lib/Form";
import { APPOINTMENT_NOTE_TYPES, STATUS_IDENTIFIERS } from "models";
import { enumToDropdownOptions } from "utils/common";

import type { LocationDetailFormPayload } from "modules/LocationOverviewDashboard/hooks";

const FormHelper = createFormHelper<LocationDetailFormPayload>();

const NOTE_TYPES = [
  { value: APPOINTMENT_NOTE_TYPES.RecurringCar, labelKey: "v8_recurring_car", labelFallback: "Recurring Car" },
  { value: APPOINTMENT_NOTE_TYPES.BackOrder, labelKey: "v8_backorder", labelFallback: "Backorder" },
  { value: APPOINTMENT_NOTE_TYPES.Info, labelKey: "v8_info", labelFallback: "Info" },
  { value: APPOINTMENT_NOTE_TYPES.Attachment, labelKey: "v8_attachment", labelFallback: "Attachment" },
  { value: APPOINTMENT_NOTE_TYPES.Bill, labelKey: "v8_bill_note", labelFallback: "Bill note" },
  { value: APPOINTMENT_NOTE_TYPES.TempDriver, labelKey: "v8_temporary_driver_note", labelFallback: "Temporary driver note" },
  { value: APPOINTMENT_NOTE_TYPES.ParkingLocation, labelKey: "v8_parking", labelFallback: "Parking" },
  { value: APPOINTMENT_NOTE_TYPES.MakeAnOffer, labelKey: "v8_make_an_offer", labelFallback: "Make an offer" },
  { value: APPOINTMENT_NOTE_TYPES.NoDrive, labelKey: "v8_no_drive_note", labelFallback: "No drive note" }
];

type WorkorderProps = {
  tabId: string;
};

export const Workorder = ({ tabId }: WorkorderProps) => {
  const { t } = useTranslation();

  const { watch, setValue } = FormHelper.useFormContext();
  const enabledNoteTypes = watch("enabled_appointment_note_type_ids") ?? [];

  const statusOptions = enumToDropdownOptions({ enumObj: STATUS_IDENTIFIERS, t }).filter(option => option.value <= STATUS_IDENTIFIERS.BackOrder);

  const toggleNoteType = (value: APPOINTMENT_NOTE_TYPES, checked?: boolean) => {
    setValue("enabled_appointment_note_type_ids", checked ? [...enabledNoteTypes, value] : enabledNoteTypes.filter(noteType => noteType !== value));
  };

  return (
    <Element name={tabId} className="location-overview-section">
      <div className="section-title">
        <div>
          <Icon className="eye" />
        </div>
        <span className="section-title-text">{t("v8_workorder", "Workorder")}</span>
      </div>

      <div className="location-overview-columns">
        <div className="location-overview-column">
          <div className="location-overview-subheading">{t("v8_appointment_pin_behaviour", "Appointment pin behaviour")}</div>
          <Form.Field>
            <label> {t("v8_unpin_appointments", "Unpin appointments")} </label>
          </Form.Field>
          <Form.Field>
            <label>{t("v8_automatically_unpin_appointments_on_selected_statuses", "Automatically unpin appointments on selected statuses")}</label>
            <FormHelper.Field.Dropdown name="auto_unpin_appointment_statuses" options={statusOptions} dropdownProps={{ multiple: true, search: true, clearable: true }} />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_unpin_appointments_older_than_days", "Unpin appointments older than [x] days")}</label>
            <FormHelper.Field.Input name="unpin_past_appointments_older_than" inputProps={{ type: "number", min: 0 }} />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_unpin_appointments_further_than_days", "Unpin appointments further than [x] days")}</label>
            <FormHelper.Field.Input name="unpin_future_appointments_further_than" inputProps={{ type: "number", min: 0 }} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="unpin_appointments_on_is_billed" label={t("v8_unpin_appointment_on_is_billed", "Unpin Appointment On Is Billed")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox
              name="unpin_appointments_on_car_out_of_shop"
              label={t("v8_unpin_appointment_on_car_out_of_shop", "Unpin Appointment On Car Out of Shop")}
            />
          </Form.Field>
        </div>

        <div className="location-overview-column">
          <div className="location-overview-subheading">{t("v8_additional_appointment_note_types", "Additional Appointment Note Types")}</div>
          {NOTE_TYPES.map(noteType => (
            <Form.Field key={noteType.value}>
              <Checkbox
                label={t(noteType.labelKey, noteType.labelFallback)}
                checked={enabledNoteTypes.includes(noteType.value)}
                onChange={(_, { checked }) => toggleNoteType(noteType.value, checked)}
              />
            </Form.Field>
          ))}
        </div>
      </div>
    </Element>
  );
};
